import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Users, Plus, Edit, Trash2, Search, RefreshCw, ChevronDown, ChevronRight,
  History, IndianRupee, UserX, UserMinus, Calendar, Building2, Settings,
  Wallet, CheckCircle2, XCircle, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import SalaryIncrementDialog from "@/components/SalaryIncrementDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["Employed", "On Leave", "Relieved", "Absconded", "Fired"];

const emptyEmpForm = {
  name: "", monthly_salary: "", status: "Employed", category: "CPI",
  leave_from_date: "", leave_to_date: "",
};
const emptySalaryForm = {
  employee_name: "", salary_type: "regular", shift_hours: "8",
  basic_pay: "", da: "0", hra: "0", conveyance: "0", other_allowances: "0",
  daily_rate: "0",
  pf_enabled: true, pf_percent: "12",
  esi_enabled: true, esi_percent: "0.75",
  pt_enabled: true, pt: "200",
  tds_enabled: false, tds_percent: "0",
};

const HREmployeeSalary = () => {
  const [employees, setEmployees] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Dialogs
  const [empDialogOpen, setEmpDialogOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState({ ...emptyEmpForm });
  const [empFormTab, setEmpFormTab] = useState("info");
  const [salaryForm, setSalaryForm] = useState({ ...emptySalaryForm });
  const [existingSalaryId, setExistingSalaryId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteEmpId, setDeleteEmpId] = useState(null);

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEmpId, setHistoryEmpId] = useState(null);
  const [historyForm, setHistoryForm] = useState({
    status: "On Leave", reason: "", from_date: "", to_date: "",
  });

  // Increment dialog state
  const [incrementDialogOpen, setIncrementDialogOpen] = useState(false);
  const [incrementMode, setIncrementMode] = useState("single"); // "single" | "bulk"
  const [incrementTargetEmp, setIncrementTargetEmp] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [empRes, cfgRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/hr/salary-configs`),
      ]);
      setEmployees(empRes.data);
      setConfigs(cfgRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
    toast.success("Refreshed");
  };

  // Lookup helpers
  const configMap = {};
  configs.forEach(c => { configMap[c.employee_name] = c; });
  const getConfig = (name) => configMap[name] || null;

  // Stats
  const activeCPI = employees.filter(e => e.category === "CPI" && e.status === "Employed").length;
  const activeTW = employees.filter(e => e.category === "TW" && e.status === "Employed").length;
  const relievedCount = employees.filter(e => e.status === "Relieved").length;
  const firedCount = employees.filter(e => e.status === "Fired").length;
  const onLeaveCount = employees.filter(e => e.status === "On Leave").length;
  const totalSalary = employees.reduce((sum, e) => sum + (e.monthly_salary || 0), 0);
  const configuredCount = employees.filter(e => configMap[e.name]).length;
  const unconfiguredCount = employees.length - configuredCount;

  // Filter
  const filteredEmployees = employees.filter(e => {
    let matchesFilter = true;
    if (activeFilter) {
      switch (activeFilter) {
        case "cpi": matchesFilter = e.category === "CPI" && e.status === "Employed"; break;
        case "tw": matchesFilter = e.category === "TW" && e.status === "Employed"; break;
        case "relieved": matchesFilter = e.status === "Relieved"; break;
        case "fired": matchesFilter = e.status === "Fired"; break;
        case "onleave": matchesFilter = e.status === "On Leave"; break;
        case "unconfigured": matchesFilter = !configMap[e.name]; break;
        default: matchesFilter = true;
      }
    }
    if (!search) return matchesFilter;
    const term = search.toLowerCase();
    return matchesFilter && (e.name?.toLowerCase().includes(term) || e.category?.toLowerCase().includes(term));
  });

  // Status badge color
  const statusColor = (s) => {
    switch (s) {
      case "Employed": return "bg-green-500/20 text-green-400 border-green-500/40";
      case "On Leave": return "bg-amber-500/20 text-amber-400 border-amber-500/40";
      case "Rejoined": return "bg-cyan-500/20 text-cyan-400 border-cyan-500/40";
      case "Relieved": return "bg-gray-500/20 text-gray-400 border-gray-500/40";
      case "Absconded": return "bg-orange-500/20 text-orange-400 border-orange-500/40";
      case "Fired": return "bg-red-500/20 text-red-400 border-red-500/40";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/40";
    }
  };

  // Add/Edit Employee + Salary
  const openAdd = () => {
    setEditingEmp(null);
    setEmpForm({ ...emptyEmpForm });
    setSalaryForm({ ...emptySalaryForm });
    setExistingSalaryId(null);
    setEmpFormTab("info");
    setEmpDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditingEmp(emp);
    setEmpForm({
      name: emp.name,
      monthly_salary: emp.monthly_salary || "",
      status: emp.status || "Employed",
      category: emp.category || "CPI",
      leave_from_date: emp.leave_from_date || "",
      leave_to_date: emp.leave_to_date || "",
    });
    const cfg = getConfig(emp.name);
    if (cfg) {
      setSalaryForm({
        employee_name: cfg.employee_name,
        salary_type: cfg.salary_type || "regular",
        shift_hours: String(cfg.shift_hours || 8),
        basic_pay: String(cfg.basic_pay || ""),
        da: String(cfg.da || 0),
        hra: String(cfg.hra || 0),
        conveyance: String(cfg.conveyance || 0),
        other_allowances: String(cfg.other_allowances || 0),
        daily_rate: String(cfg.daily_rate || 0),
        pf_enabled: cfg.pf_enabled !== false, pf_percent: String(cfg.pf_percent ?? 12),
        esi_enabled: cfg.esi_enabled !== false, esi_percent: String(cfg.esi_percent ?? 0.75),
        pt_enabled: cfg.pt_enabled !== false, pt: String(cfg.pt ?? 200),
        tds_enabled: !!cfg.tds_enabled, tds_percent: String(cfg.tds_percent || 0),
      });
      setExistingSalaryId(cfg.id);
    } else {
      setSalaryForm({ ...emptySalaryForm, employee_name: emp.name });
      setExistingSalaryId(null);
    }
    setEmpFormTab("info");
    setEmpDialogOpen(true);
  };

  const saveEmployee = async () => {
    if (!empForm.name.trim()) {
      toast.error("Employee name is required");
      setEmpFormTab("info");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: empForm.name,
        monthly_salary: empForm.monthly_salary ? parseFloat(empForm.monthly_salary) : 0,
        status: empForm.status,
        category: empForm.category,
        leave_from_date: empForm.status === "On Leave" ? empForm.leave_from_date : null,
        leave_to_date: empForm.status === "On Leave" ? empForm.leave_to_date : null,
      };
      let savedName = empForm.name;
      if (editingEmp) {
        await axios.put(`${API}/employees/${editingEmp.id}`, payload);
      } else {
        await axios.post(`${API}/employees`, payload);
      }

      // Save/update salary config if basic_pay or daily_rate provided
      const hasSalaryData = (salaryForm.salary_type !== "daily" && salaryForm.basic_pay)
        || (salaryForm.salary_type === "daily" && salaryForm.daily_rate && parseFloat(salaryForm.daily_rate) > 0);
      if (hasSalaryData) {
        const salaryPayload = { ...salaryForm, employee_name: savedName };
        if (existingSalaryId) {
          await axios.put(`${API}/hr/salary-configs/${existingSalaryId}`, salaryPayload);
        } else {
          await axios.post(`${API}/hr/salary-configs`, salaryPayload);
        }
      }

      toast.success(editingEmp ? "Employee updated" : "Employee added");
      setEmpDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const deleteEmployee = async () => {
    if (!deleteEmpId) return;
    try {
      await axios.delete(`${API}/employees/${deleteEmpId}`);
      // Optionally delete salary config too
      const emp = employees.find(e => e.id === deleteEmpId);
      const cfg = emp ? getConfig(emp.name) : null;
      if (cfg) {
        try { await axios.delete(`${API}/hr/salary-configs/${cfg.id}`); } catch {}
      }
      toast.success("Employee deleted");
      setDeleteEmpId(null);
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const deleteSalaryConfig = async (cfgId) => {
    if (!window.confirm("Delete salary configuration for this employee?")) return;
    try {
      await axios.delete(`${API}/hr/salary-configs/${cfgId}`);
      toast.success("Salary config deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Status History
  const openHistoryDialog = (empId) => {
    setHistoryEmpId(empId);
    setHistoryForm({ status: "On Leave", reason: "", from_date: "", to_date: "" });
    setHistoryDialogOpen(true);
  };

  const addHistory = async () => {
    if (!historyEmpId) return;
    try {
      await axios.post(`${API}/employees/${historyEmpId}/status-history`, historyForm);
      const newStatus = historyForm.status === "Rejoined" ? "Employed" : historyForm.status;
      const updatePayload = { status: newStatus };
      if (historyForm.status === "On Leave") {
        updatePayload.leave_from_date = historyForm.from_date || null;
        updatePayload.leave_to_date = historyForm.to_date || null;
      } else {
        updatePayload.leave_from_date = null;
        updatePayload.leave_to_date = null;
      }
      await axios.put(`${API}/employees/${historyEmpId}`, updatePayload);
      toast.success(`Status updated to "${newStatus}"`);
      setHistoryDialogOpen(false);
      fetchAll();
    } catch {
      toast.error("Failed to add history");
    }
  };

  const deleteHistory = async (empId, historyId) => {
    try {
      await axios.delete(`${API}/employees/${empId}/status-history/${historyId}`);
      toast.success("History entry deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const grossCalc = (c) => (c.basic_pay || 0) + (c.da || 0) + (c.hra || 0) + (c.conveyance || 0) + (c.other_allowances || 0);

  const statCards = [
    { key: "cpi", label: "Active CPI", value: activeCPI, icon: Building2, color: "text-blue-400", bg: "bg-blue-500/20", ring: "ring-blue-500" },
    { key: "tw", label: "Active TW", value: activeTW, icon: Building2, color: "text-purple-400", bg: "bg-purple-500/20", ring: "ring-purple-500" },
    { key: "relieved", label: "Relieved", value: relievedCount, icon: UserMinus, color: "text-gray-400", bg: "bg-gray-500/20", ring: "ring-gray-400" },
    { key: "fired", label: "Fired", value: firedCount, icon: UserX, color: "text-red-400", bg: "bg-red-500/20", ring: "ring-red-500" },
    { key: "onleave", label: "On Leave", value: onLeaveCount, icon: Calendar, color: "text-amber-400", bg: "bg-amber-500/20", ring: "ring-amber-500" },
    { key: "unconfigured", label: "No Salary Config", value: unconfiguredCount, icon: Settings, color: "text-rose-400", bg: "bg-rose-500/20", ring: "ring-rose-500" },
  ];

  return (
    <div className="space-y-6" data-testid="hr-employee-salary-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-1 bg-gradient-to-b from-teal-400 to-emerald-400 rounded-full" />
            <h2 className="text-2xl font-bold text-teal-300 flex items-center gap-2">
              <Users className="h-6 w-6" /> Employees & Salary Configuration
            </h2>
          </div>
          <p className="text-gray-400 text-sm ml-3 mt-1">
            {employees.length} total · {configuredCount} with salary config · ₹{totalSalary.toLocaleString("en-IN")} total payroll
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} variant="outline" disabled={refreshing} className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="btn-refresh">
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
          <Button
            onClick={() => { setIncrementMode("bulk"); setIncrementTargetEmp(null); setIncrementDialogOpen(true); }}
            variant="outline"
            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
            data-testid="btn-bulk-increment"
          >
            <TrendingUp className="h-4 w-4 mr-2" /> Bulk Increment
          </Button>
          <Button onClick={openAdd} className="bg-teal-600 hover:bg-teal-700" data-testid="btn-add-employee">
            <Plus className="h-4 w-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map(c => {
          const Icon = c.icon;
          const active = activeFilter === c.key;
          return (
            <Card
              key={c.key}
              onClick={() => setActiveFilter(active ? null : c.key)}
              className={cn(
                "cursor-pointer transition-all hover:scale-[1.03] bg-gray-900/40",
                active ? `border-transparent ring-2 ${c.ring}` : "border-gray-800 hover:border-gray-600"
              )}
              data-testid={`stat-${c.key}`}
            >
              <CardContent className="p-3 flex flex-col items-center text-center">
                <div className={`p-2 rounded-lg mb-1 ${c.bg}`}>
                  <Icon className={`h-5 w-5 ${c.color}`} />
                </div>
                <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{c.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Active filter indicator */}
      {activeFilter && (
        <div className="flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-lg" data-testid="active-filter">
          <span className="text-teal-300 text-sm">
            Filter: {statCards.find(s => s.key === activeFilter)?.label}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setActiveFilter(null)} className="text-teal-300 hover:text-teal-200 h-6 px-2">
            Clear
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or category..."
          className="pl-9 bg-gray-900/40 border-gray-700 text-white h-9"
          data-testid="search-input"
        />
      </div>

      {/* Employee table */}
      <Card className="bg-gray-900/40 border-gray-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
              <p className="mt-3 text-sm">Loading...</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12" data-testid="no-employees">
              <Users className="mx-auto h-14 w-14 text-gray-700" />
              <p className="text-gray-400 mt-3">No employees found</p>
              <p className="text-gray-500 text-xs mt-1">
                {search || activeFilter ? "Try a different search or filter" : "Add your first employee to get started"}
              </p>
              {!search && !activeFilter && (
                <Button onClick={openAdd} className="mt-4 bg-teal-600 hover:bg-teal-700">
                  <Plus className="h-4 w-4 mr-2" /> Add First Employee
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filteredEmployees.map((emp) => {
                const cfg = getConfig(emp.name);
                return (
                  <Collapsible
                    key={emp.id}
                    open={expandedId === emp.id}
                    onOpenChange={(open) => setExpandedId(open ? emp.id : null)}
                  >
                    <div className="p-3 hover:bg-gray-800/30 transition-colors" data-testid={`emp-row-${emp.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="p-1 h-7 w-7 shrink-0">
                              {expandedId === emp.id ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            </Button>
                          </CollapsibleTrigger>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-medium truncate">{emp.name}</p>
                              <Badge variant="outline" className={cn("text-[10px]", emp.category === "CPI" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-purple-500/20 text-purple-300 border-purple-500/40")}>
                                {emp.category || "CPI"}
                              </Badge>
                              <Badge variant="outline" className={cn("text-[10px]", statusColor(emp.status))}>{emp.status}</Badge>
                              {cfg ? (
                                <Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                                  <CheckCircle2 className="h-3 w-3 mr-0.5" />
                                  {cfg.salary_type === "regular" ? "Regular" : cfg.salary_type === "contract" ? "Contract" : "Daily"} · {cfg.shift_hours || 8}h
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-rose-500/15 text-rose-300 border-rose-500/40">
                                  <XCircle className="h-3 w-3 mr-0.5" /> No Salary Config
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs">
                              <span className="text-gray-400">
                                <IndianRupee className="h-3 w-3 inline" />
                                {emp.monthly_salary ? emp.monthly_salary.toLocaleString("en-IN") : "0"}<span className="text-gray-600"> base</span>
                              </span>
                              {cfg && (
                                <span className="text-emerald-400 font-mono">
                                  Gross ₹{grossCalc(cfg).toLocaleString("en-IN")}
                                </span>
                              )}
                              {emp.status === "On Leave" && emp.leave_from_date && (
                                <span className="text-amber-400">Leave: {emp.leave_from_date} → {emp.leave_to_date || "TBD"}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="text-teal-300 hover:bg-teal-500/10 h-8 px-2" onClick={() => openEdit(emp)} data-testid={`edit-emp-${emp.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-8 px-2" onClick={() => setDeleteEmpId(emp.id)} data-testid={`delete-emp-${emp.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    <CollapsibleContent>
                      <div className="px-4 pb-4 pt-1 bg-black/30 border-t border-gray-800/60 space-y-4">
                        {/* Salary Config Details */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                              <Wallet className="h-3.5 w-3.5 text-amber-400" /> Salary Configuration
                            </h4>
                            <div className="flex items-center gap-1">
                              {cfg && (
                                <Button size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10 h-7 px-2 text-xs" onClick={() => { setIncrementMode("single"); setIncrementTargetEmp(emp); setIncrementDialogOpen(true); }} data-testid={`increment-${emp.id}`}>
                                  <TrendingUp className="h-3 w-3 mr-1" /> Apply Increment
                                </Button>
                              )}
                              {cfg ? (
                                <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-7 px-2 text-xs" onClick={() => deleteSalaryConfig(cfg.id)} data-testid={`delete-cfg-${cfg.id}`}>
                                  <Trash2 className="h-3 w-3 mr-1" /> Remove Config
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="text-teal-300 hover:bg-teal-500/10 h-7 px-2 text-xs" onClick={() => openEdit(emp)} data-testid={`configure-${emp.id}`}>
                                  <Plus className="h-3 w-3 mr-1" /> Configure Salary
                                </Button>
                              )}
                            </div>
                          </div>
                          {cfg ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
                                <div className="text-gray-500">Type / Shift</div>
                                <div className="text-gray-200 font-medium mt-0.5">{cfg.salary_type} · {cfg.shift_hours || 8}h</div>
                              </div>
                              {cfg.salary_type !== "daily" ? (
                                <>
                                  <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
                                    <div className="text-gray-500">Basic</div>
                                    <div className="text-emerald-300 font-mono mt-0.5">₹{(cfg.basic_pay || 0).toLocaleString("en-IN")}</div>
                                  </div>
                                  <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
                                    <div className="text-gray-500">DA / HRA</div>
                                    <div className="text-gray-200 font-mono mt-0.5">₹{(cfg.da || 0).toLocaleString("en-IN")} / ₹{(cfg.hra || 0).toLocaleString("en-IN")}</div>
                                  </div>
                                  <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
                                    <div className="text-gray-500">Conv. / Other</div>
                                    <div className="text-gray-200 font-mono mt-0.5">₹{(cfg.conveyance || 0).toLocaleString("en-IN")} / ₹{(cfg.other_allowances || 0).toLocaleString("en-IN")}</div>
                                  </div>
                                </>
                              ) : (
                                <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
                                  <div className="text-gray-500">Daily Rate</div>
                                  <div className="text-amber-300 font-mono mt-0.5">₹{(cfg.daily_rate || 0).toLocaleString("en-IN")}/day</div>
                                </div>
                              )}
                              <div className="bg-gray-900/60 rounded p-2 border border-gray-800 col-span-2 md:col-span-4">
                                <div className="text-gray-500 mb-1">Deductions</div>
                                <div className="flex flex-wrap gap-2 text-[11px]">
                                  <span className={cfg.pf_enabled !== false ? "text-emerald-300" : "text-gray-600"}>PF: {cfg.pf_enabled !== false ? `${cfg.pf_percent}%` : "OFF"}</span>
                                  <span className="text-gray-700">|</span>
                                  <span className={cfg.esi_enabled !== false ? "text-emerald-300" : "text-gray-600"}>ESI: {cfg.esi_enabled !== false ? `${cfg.esi_percent}%` : "OFF"}</span>
                                  <span className="text-gray-700">|</span>
                                  <span className={cfg.pt_enabled !== false ? "text-emerald-300" : "text-gray-600"}>PT: {cfg.pt_enabled !== false ? `₹${cfg.pt}` : "OFF"}</span>
                                  <span className="text-gray-700">|</span>
                                  <span className={cfg.tds_enabled ? "text-emerald-300" : "text-gray-600"}>TDS: {cfg.tds_enabled ? `${cfg.tds_percent}%` : "OFF"}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-xs py-2">Salary not configured. Payroll cannot be generated for this employee.</p>
                          )}
                        </div>

                        {/* Status History */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                              <History className="h-3.5 w-3.5 text-cyan-400" /> Status History
                            </h4>
                            <Button size="sm" onClick={() => openHistoryDialog(emp.id)} className="bg-cyan-600 hover:bg-cyan-700 h-7 text-xs" data-testid={`add-history-${emp.id}`}>
                              <Plus className="h-3 w-3 mr-1" /> Add Entry
                            </Button>
                          </div>
                          {emp.status_history && emp.status_history.length > 0 ? (
                            <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-gray-800 hover:bg-transparent">
                                    <TableHead className="text-gray-500 text-[10px] uppercase h-8">Status</TableHead>
                                    <TableHead className="text-gray-500 text-[10px] uppercase h-8">Reason</TableHead>
                                    <TableHead className="text-gray-500 text-[10px] uppercase h-8">From</TableHead>
                                    <TableHead className="text-gray-500 text-[10px] uppercase h-8">To</TableHead>
                                    <TableHead className="text-gray-500 text-[10px] uppercase h-8 text-right">—</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {emp.status_history.map((h) => (
                                    <TableRow key={h.id} className="border-gray-800 hover:bg-gray-800/40">
                                      <TableCell className="py-2">
                                        <Badge variant="outline" className={cn("text-[10px]", statusColor(h.status))}>{h.status}</Badge>
                                      </TableCell>
                                      <TableCell className="text-gray-300 text-xs py-2">{h.reason || "—"}</TableCell>
                                      <TableCell className="text-gray-300 text-xs py-2">{h.from_date || "—"}</TableCell>
                                      <TableCell className="text-gray-300 text-xs py-2">{h.to_date || "—"}</TableCell>
                                      <TableCell className="text-right py-2">
                                        <Button variant="ghost" size="sm" onClick={() => deleteHistory(emp.id, h.id)} className="text-red-400 hover:text-red-300 h-7 w-7 p-0">
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-xs py-2">No status history recorded.</p>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee + Salary Dialog with Tabs */}
      <Dialog open={empDialogOpen} onOpenChange={setEmpDialogOpen}>
        <DialogContent className="bg-gray-900 border-teal-500/40 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="emp-dialog">
          <DialogHeader>
            <DialogTitle className="text-teal-300 flex items-center gap-2">
              {editingEmp ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editingEmp ? `Edit — ${editingEmp.name}` : "Add New Employee"}
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              Configure both employee master details and salary structure in one place.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={empFormTab} onValueChange={setEmpFormTab} className="mt-2">
            <TabsList className="bg-gray-800 border border-gray-700">
              <TabsTrigger value="info" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-info">
                <Users className="h-4 w-4 mr-1.5" /> Employee Info
              </TabsTrigger>
              <TabsTrigger value="salary" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-salary">
                <Wallet className="h-4 w-4 mr-1.5" /> Salary Configuration
              </TabsTrigger>
            </TabsList>

            {/* INFO TAB */}
            <TabsContent value="info" className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Employee Name *</Label>
                <Input value={empForm.name} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} placeholder="John Doe" className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-name" />
              </div>
              <div>
                <Label className="text-gray-300">Monthly Salary (Employee Master)</Label>
                <Input type="number" step="0.01" value={empForm.monthly_salary} onChange={e => setEmpForm({ ...empForm, monthly_salary: e.target.value })} placeholder="30000" className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-monthly-salary" />
                <p className="text-[10px] text-gray-500 mt-1">This is the ballpark salary shown in the roster. Actual payroll is calculated from the Salary Configuration tab.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">Category *</Label>
                  <Select value={empForm.category} onValueChange={(v) => setEmpForm({ ...empForm, category: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="select-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem value="CPI" className="text-white">CPI</SelectItem>
                      <SelectItem value="TW" className="text-white">TW</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Employment Status *</Label>
                  <Select value={empForm.status} onValueChange={(v) => setEmpForm({ ...empForm, status: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="select-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {empForm.status === "On Leave" && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <div>
                    <Label className="text-amber-300 text-xs">Leave From</Label>
                    <Input type="date" value={empForm.leave_from_date} onChange={e => setEmpForm({ ...empForm, leave_from_date: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-amber-300 text-xs">Leave To</Label>
                    <Input type="date" value={empForm.leave_to_date} onChange={e => setEmpForm({ ...empForm, leave_to_date: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* SALARY TAB */}
            <TabsContent value="salary" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">Salary Type</Label>
                  <Select value={salaryForm.salary_type} onValueChange={(v) => setSalaryForm({ ...salaryForm, salary_type: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="select-salary-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem value="regular" className="text-white">Regular Basis</SelectItem>
                      <SelectItem value="contract" className="text-white">Contract Basis</SelectItem>
                      <SelectItem value="daily" className="text-white">Daily Basis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Shift Hours</Label>
                  <Select value={salaryForm.shift_hours} onValueChange={(v) => setSalaryForm({ ...salaryForm, shift_hours: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="select-shift"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem value="8" className="text-white">8 Hour Shift</SelectItem>
                      <SelectItem value="10" className="text-white">10 Hour Shift</SelectItem>
                      <SelectItem value="12" className="text-white">12 Hour Shift</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {salaryForm.salary_type !== "daily" ? (
                <>
                  <div>
                    <Label className="text-gray-300">Basic Pay (Monthly) *</Label>
                    <Input type="number" value={salaryForm.basic_pay} onChange={e => setSalaryForm({ ...salaryForm, basic_pay: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-basic-pay" />
                  </div>
                  {salaryForm.salary_type === "regular" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-gray-300 text-xs">DA</Label><Input type="number" value={salaryForm.da} onChange={e => setSalaryForm({ ...salaryForm, da: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
                      <div><Label className="text-gray-300 text-xs">HRA</Label><Input type="number" value={salaryForm.hra} onChange={e => setSalaryForm({ ...salaryForm, hra: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
                      <div><Label className="text-gray-300 text-xs">Conveyance</Label><Input type="number" value={salaryForm.conveyance} onChange={e => setSalaryForm({ ...salaryForm, conveyance: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
                      <div><Label className="text-gray-300 text-xs">Other Allowances</Label><Input type="number" value={salaryForm.other_allowances} onChange={e => setSalaryForm({ ...salaryForm, other_allowances: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
                    </div>
                  )}
                  <div className="bg-gray-800/50 border border-gray-700 rounded-md p-2 text-[11px] text-gray-400">
                    <span className="text-teal-300">OT rate</span>: Basic Pay / Days in month / {salaryForm.shift_hours}h × 1.5 (auto)
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-gray-300">Fixed Salary Per Day *</Label>
                    <Input type="number" value={salaryForm.daily_rate} onChange={e => setSalaryForm({ ...salaryForm, daily_rate: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-daily-rate" />
                  </div>
                  <div className="bg-gray-800/50 border border-gray-700 rounded-md p-2 text-[11px] text-amber-300">
                    <span className="font-medium">Daily Basis Formula:</span> Fixed Salary/Day × Working Days = Net Pay (no deductions applied)
                  </div>
                </>
              )}

              {salaryForm.salary_type !== "daily" && (
                <div className="space-y-2">
                  <Label className="text-gray-300">Deductions</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 bg-gray-800/60 rounded-lg p-2 border border-gray-700">
                      <input type="checkbox" checked={salaryForm.pf_enabled} onChange={e => setSalaryForm({ ...salaryForm, pf_enabled: e.target.checked })} className="w-4 h-4 accent-teal-500" data-testid="chk-pf" />
                      <div className="flex-1">
                        <div className="text-white text-xs font-medium">PF</div>
                        {salaryForm.pf_enabled && <Input type="number" step="0.01" value={salaryForm.pf_percent} onChange={e => setSalaryForm({ ...salaryForm, pf_percent: e.target.value })} className="bg-gray-700 border-gray-600 text-white h-7 mt-1 text-[11px]" />}
                      </div>
                      {salaryForm.pf_enabled && <span className="text-gray-400 text-[10px]">{salaryForm.pf_percent}%</span>}
                    </div>
                    <div className="flex items-center gap-2 bg-gray-800/60 rounded-lg p-2 border border-gray-700">
                      <input type="checkbox" checked={salaryForm.esi_enabled} onChange={e => setSalaryForm({ ...salaryForm, esi_enabled: e.target.checked })} className="w-4 h-4 accent-teal-500" data-testid="chk-esi" />
                      <div className="flex-1">
                        <div className="text-white text-xs font-medium">ESI</div>
                        {salaryForm.esi_enabled && <Input type="number" step="0.01" value={salaryForm.esi_percent} onChange={e => setSalaryForm({ ...salaryForm, esi_percent: e.target.value })} className="bg-gray-700 border-gray-600 text-white h-7 mt-1 text-[11px]" />}
                      </div>
                      {salaryForm.esi_enabled && <span className="text-gray-400 text-[10px]">{salaryForm.esi_percent}%</span>}
                    </div>
                    <div className="flex items-center gap-2 bg-gray-800/60 rounded-lg p-2 border border-gray-700">
                      <input type="checkbox" checked={salaryForm.pt_enabled} onChange={e => setSalaryForm({ ...salaryForm, pt_enabled: e.target.checked })} className="w-4 h-4 accent-teal-500" data-testid="chk-pt" />
                      <div className="flex-1">
                        <div className="text-white text-xs font-medium">Professional Tax</div>
                        {salaryForm.pt_enabled && <Input type="number" value={salaryForm.pt} onChange={e => setSalaryForm({ ...salaryForm, pt: e.target.value })} className="bg-gray-700 border-gray-600 text-white h-7 mt-1 text-[11px]" />}
                      </div>
                      {salaryForm.pt_enabled && <span className="text-gray-400 text-[10px]">₹{salaryForm.pt}</span>}
                    </div>
                    <div className="flex items-center gap-2 bg-gray-800/60 rounded-lg p-2 border border-gray-700">
                      <input type="checkbox" checked={salaryForm.tds_enabled} onChange={e => setSalaryForm({ ...salaryForm, tds_enabled: e.target.checked })} className="w-4 h-4 accent-teal-500" data-testid="chk-tds" />
                      <div className="flex-1">
                        <div className="text-white text-xs font-medium">TDS</div>
                        {salaryForm.tds_enabled && <Input type="number" step="0.01" value={salaryForm.tds_percent} onChange={e => setSalaryForm({ ...salaryForm, tds_percent: e.target.value })} className="bg-gray-700 border-gray-600 text-white h-7 mt-1 text-[11px]" />}
                      </div>
                      {salaryForm.tds_enabled && <span className="text-gray-400 text-[10px]">{salaryForm.tds_percent}%</span>}
                    </div>
                  </div>
                </div>
              )}

              {!editingEmp && (
                <p className="text-[11px] text-gray-500 italic">Tip: Salary config will only be created if Basic Pay / Daily Rate is filled.</p>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEmpDialogOpen(false)} disabled={saving} className="border-gray-600 text-gray-300" data-testid="btn-cancel">Cancel</Button>
            <Button onClick={saveEmployee} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="btn-save">
              {saving ? "Saving..." : editingEmp ? "Save Changes" : "Add Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-300 flex items-center gap-2">
              <History className="h-5 w-5" /> Add Status History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-gray-300">Status</Label>
              <Select value={historyForm.status} onValueChange={(v) => setHistoryForm({ ...historyForm, status: v })}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="On Leave" className="text-white">On Leave</SelectItem>
                  <SelectItem value="Rejoined" className="text-white">Rejoined</SelectItem>
                  <SelectItem value="Relieved" className="text-white">Relieved</SelectItem>
                  <SelectItem value="Absconded" className="text-white">Absconded</SelectItem>
                  <SelectItem value="Fired" className="text-white">Fired</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-gray-500 mt-1">
                {historyForm.status === "Rejoined" ? "Employee status will be set to 'Employed'" : `Employee status will be set to '${historyForm.status}'`}
              </p>
            </div>
            <div>
              <Label className="text-gray-300">Reason / Comments</Label>
              <Textarea value={historyForm.reason} onChange={e => setHistoryForm({ ...historyForm, reason: e.target.value })} rows={3} className="bg-gray-800 border-gray-600 text-white mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs">From Date</Label>
                <Input type="date" value={historyForm.from_date} onChange={e => setHistoryForm({ ...historyForm, from_date: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs">To Date</Label>
                <Input type="date" value={historyForm.to_date} onChange={e => setHistoryForm({ ...historyForm, to_date: e.target.value })} className="bg-gray-800 border-gray-600 text-white mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={addHistory} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-save-history">Add History</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEmpId} onOpenChange={() => setDeleteEmpId(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Employee</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete the employee and their salary configuration (if any). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 text-gray-300 hover:bg-gray-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteEmployee} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Salary Increment Dialog */}
      <SalaryIncrementDialog
        open={incrementDialogOpen}
        onOpenChange={setIncrementDialogOpen}
        mode={incrementMode}
        employee={incrementTargetEmp}
        allEmployees={employees}
        onApplied={fetchAll}
      />
    </div>
  );
};

export default HREmployeeSalary;
